import { getApiBaseUrl } from "@/config/runtime";
import type {
  DataResponse,
  MicroApp,
  MicroAppAuthorization,
  MicroAppBindingData,
  MicroAppBindingsPreview,
  MicroAppInfo,
  MicroAppMembers,
  MicroAppPublish,
  MicroAppRole,
  MicroAppSummary,
  MicroAppTeamMember,
  MicroAppUpdate,
  MicroAppUserMember,
  PaginatedResponse,
} from "@/types";
import { api, getHeaders } from "../api";

const PREFIX = "/micro-apps";

export const microAppService = {
  list: (page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<MicroAppSummary>>(`${PREFIX}?page=${page}&pageSize=${pageSize}`),

  get: (uuid: string) => api.get<DataResponse<MicroApp>>(`${PREFIX}/${uuid}`),

  publish: (data: MicroAppPublish) => api.post<DataResponse<MicroApp>>(`${PREFIX}/publish`, data),

  /** Pre-publish read of an attachment's data bindings (grouped per source). */
  getAttachmentBindings: (attachmentUuid: string) =>
    api.get<DataResponse<MicroAppBindingsPreview>>(`${PREFIX}/attachments/${attachmentUuid}/bindings`),

  /** Which published app (if any) an HTML attachment belongs to — for the "Open micro app" link. */
  getAppForAttachment: (attachmentUuid: string) =>
    api.get<DataResponse<{ app_uuid: string | null }>>(`${PREFIX}/attachments/${attachmentUuid}/app`),

  /** Replace a published app's content from a newly generated attachment. */
  republish: (uuid: string, attachmentUuid: string) =>
    api.post<DataResponse<MicroApp>>(`${PREFIX}/${uuid}/republish`, { attachmentUuid }),

  update: (uuid: string, data: MicroAppUpdate) => api.put<DataResponse<MicroApp>>(`${PREFIX}/${uuid}`, data),

  delete: (uuid: string) => api.delete<{ message: string }>(`${PREFIX}/${uuid}`),

  /** Fetch the rendered HTML content of a micro app (for the iframe viewer). */
  getContent: async (uuid: string): Promise<string> => {
    const headers = await getHeaders();
    headers.delete("Content-Type");
    // `no-cache` forces revalidation against the server on every load (sending
    // If-None-Match), so a re-published dashboard is picked up immediately while
    // unchanged content still returns a cheap 304.
    const response = await fetch(`${getApiBaseUrl()}${PREFIX}/${uuid}/content`, { headers, cache: "no-cache" });
    if (!response.ok) {
      throw new Error(`Failed to load micro app content (${response.status})`);
    }
    return response.text();
  },

  /** Fetch live data for a single dashboard binding (viewer hydration). */
  getBindingData: (uuid: string, bindingKey: string, signal?: AbortSignal) =>
    api.get<DataResponse<MicroAppBindingData>>(`${PREFIX}/${uuid}/data/${encodeURIComponent(bindingKey)}`, { signal }),

  /** Fetch the end-user Info/About panel (identity + authorship + provenance). */
  getInfo: (uuid: string) => api.get<DataResponse<MicroAppInfo>>(`${PREFIX}/${uuid}/info`),

  /** Author-delegated data authorization status for this app. */
  getAuthorization: (uuid: string) => api.get<DataResponse<MicroAppAuthorization>>(`${PREFIX}/${uuid}/authorization`),
  /** Capture the owner's delegated token so author-mode bindings stay live. */
  authorize: (uuid: string) => api.post<DataResponse<MicroAppAuthorization>>(`${PREFIX}/${uuid}/authorization`),
};

/** Membership (access) management — mirrors agentMembershipService. */
export const microAppMembershipService = {
  getMembers: (uuid: string) => api.get<DataResponse<MicroAppMembers>>(`${PREFIX}/${uuid}/members`),

  addUserMember: (uuid: string, userUuid: string, role: MicroAppRole) =>
    api.post<DataResponse<MicroAppUserMember>>(`${PREFIX}/${uuid}/members/users`, { userUuid, role }),
  updateUserMemberRole: (uuid: string, userUuid: string, role: MicroAppRole) =>
    api.patch<DataResponse<MicroAppUserMember>>(`${PREFIX}/${uuid}/members/users/${userUuid}`, { role }),
  removeUserMember: (uuid: string, userUuid: string) => api.delete(`${PREFIX}/${uuid}/members/users/${userUuid}`),

  addTeamMember: (uuid: string, teamUuid: string, role: MicroAppRole) =>
    api.post<DataResponse<MicroAppTeamMember>>(`${PREFIX}/${uuid}/members/teams`, { teamUuid, role }),
  updateTeamMemberRole: (uuid: string, teamUuid: string, role: MicroAppRole) =>
    api.patch<DataResponse<MicroAppTeamMember>>(`${PREFIX}/${uuid}/members/teams/${teamUuid}`, { role }),
  removeTeamMember: (uuid: string, teamUuid: string) => api.delete(`${PREFIX}/${uuid}/members/teams/${teamUuid}`),
};
